/* pauseAd.tsx */
import AnalyticsInstance, { AnalyticEventType } from '@shared/utils/analytics';
import Focus from '@shared/types/enums/focus';
import FullScreenImage from '@components/atoms/images/fullScreenImage';
import PauseAdInfo from '@components/elements/pauseAds/pauseAdInfo';
import PauseAdContainer from '@components/atoms/container/pauseAdContainer';
import PauseAdKeepWatchingText from '@components/atoms/text/pauseAdKeepWatchingText';
import PauseAdPauseIconContainer from '@components/atoms/container/pauseAdPauseIconContainer';
import PauseAdText from '@components/atoms/text/pauseAdText';
import PauseIcon from '@components/atoms/icons/pause';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { AdSdkProvider } from '@shared/types/enums/ads';
import { adsSelector, clearPauseAd } from '@shared/store/slices/ads.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';
import {
  filterFocus,
  popFocus,
  pushFocus,
} from '@shared/store/slices/focus.slice';

/**
 * Component that displays pause ads when the player is paused.
 * Shows ad background image, event information, pause icon, and keep watching text.
 * Handles keyboard interactions to resume playback or open floating menu.
 *
 * @returns {JSX.Element} Pause ad with full-screen ad and event information
 */
export default function PauseAd() {
  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { pauseAd } = useAppSelector(adsSelector);
  const { asset, background, sdkProvider } = pauseAd || {};

  // Refs
  const wasAdShown = useRef(sdkProvider === AdSdkProvider.TEADS);

  // ------------------------------
  // Effect hooks
  // ------------------------------

  useEffect(() => {
    return () => {
      wasAdShown.current &&
        AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_DISMISS);
      dispatch(clearPauseAd());
    };
  }, []);

  useEffect(() => {
    if (pauseAd?.active) {
      setTimeout(() => {
        dispatch(pushFocus(Focus.PAUSE_ADS));
      });
    }
    return () => {
      const { focus } = store.getState();
      if (focus.focus === Focus.PAUSE_ADS) {
        dispatch(popFocus());
      } else {
        dispatch(filterFocus(Focus.PAUSE_ADS));
      }
    };
  }, [pauseAd]);

  // ------------------------------
  // Event handlers
  // ------------------------------

  const onLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = event.target as HTMLImageElement;
    target.style.opacity = '1';
    AnalyticsInstance.getOmnitrackManager()
      ?.getPauseAdSession()
      ?.setWasAdShown(true);
    AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
    wasAdShown.current = true;
  };

  const onError = () => {
    AnalyticsInstance.getOmnitrackManager()
      ?.getPauseAdSession()
      ?.setWasAdShown(false);
    AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
    wasAdShown.current = false;
  };

  // ------------------------------
  // Return
  // ------------------------------

  return (
    <PauseAdContainer
      delay={pauseAd ? 1000 : 0}
      duration={pauseAd ? 300 : 0}
      opacity={pauseAd ? 1 : 0}
    >
      {background ? (
        <FullScreenImage src={background} onLoad={onLoad} onError={onError} />
      ) : null}
      <PauseAdInfo asset={asset} />
      <PauseAdPauseIconContainer>
        <PauseIcon height={55} width={34} />
      </PauseAdPauseIconContainer>
      <PauseAdKeepWatchingText>
        {TranslationManager.get('COMPONENTS.RAILS.TITLE.KEEP_WATCHING')}
      </PauseAdKeepWatchingText>
      <PauseAdText>
        {TranslationManager.get('COMPONENTS.ADS.GENERIC')}
      </PauseAdText>
    </PauseAdContainer>
  );
}
